import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Award, Fingerprint, Inbox, KeyRound, UserRound } from 'lucide-react';
import { useApi, type MyOrg } from '@/lib/api';
import { useMe } from '@/lib/me';
import { useCanUsePasskeys, usePasskeys } from '@/lib/passkeys';
import { isManager } from '@/lib/org-roles';
import { ORG_TYPE_LABELS, ROLE_LABELS } from '@/lib/labels';
import { Badge, Card, ErrorText, GuidanceNote, PageHeader } from '@/components/ui';

/**
 * Dashboard. Learners see their organizations and how to join one; org admins
 * also see seat usage per organization; platform admins see what needs review.
 * Creating or requesting an organization is rare, so it's a link, not a form.
 */
export function HomePage() {
  const api = useApi();
  const me = useMe();
  const orgs = useQuery({ queryKey: ['myOrgs'], queryFn: api.myOrgs });
  const overview = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: api.admin.overview,
    enabled: Boolean(me.data?.isPlatformAdmin),
  });

  const creator = me.data?.creator;
  const canCreate = Boolean(creator && (creator.unlimited || creator.used < creator.maxOrgs));

  return (
    <div className="space-y-8">
      <PageHeader title="Welcome back" description={me.data?.email} />

      <AccountReminders />

      {overview.data && overview.data.pendingRequests > 0 && (
        <Link to="/admin/requests" className="block">
          <Card className="flex items-center justify-between gap-4 border-primary hover:bg-muted">
            <span className="flex items-center gap-3">
              <Inbox className="size-5 text-primary" aria-hidden />
              <span>
                <span className="font-medium">
                  {overview.data.pendingRequests} organization request
                  {overview.data.pendingRequests === 1 ? '' : 's'}
                </span>{' '}
                waiting for review
              </span>
            </span>
            <ArrowRight className="size-4 text-muted-foreground" aria-hidden />
          </Card>
        </Link>
      )}

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Your organizations</h2>
          <Link to="/join" className="flex items-center gap-1.5 text-sm text-primary">
            <KeyRound className="size-4" aria-hidden />
            Have an invite code?
          </Link>
        </div>
        {orgs.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : orgs.isError ? (
          <ErrorText error={orgs.error} />
        ) : orgs.data.orgs.length === 0 ? (
          <Card>
            <p className="font-medium">You're not in an organization yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Your university or company can add you with an invite code or link. If they've
              verified their email domain, signing in with your work or university email adds you
              automatically.
            </p>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {orgs.data.orgs.map((o) => (
              <OrgCard key={o.id} org={o} />
            ))}
          </div>
        )}
      </section>

      <LearningSection />

      {!me.isPending && (
        <GuidanceNote>
          {canCreate ? (
            <>
              Setting up another organization?{' '}
              <Link to="/organizations/new" className="text-primary">
                Create an organization
              </Link>
              {creator && !creator.unlimited && ` (${creator.used} of ${creator.maxOrgs} used)`}.
            </>
          ) : (
            <>
              Bringing Nirvik Ready to your university or company?{' '}
              <Link to="/organizations/new" className="text-primary">
                Request an organization account
              </Link>
              .
            </>
          )}
        </GuidanceNote>
      )}
    </div>
  );
}

function OrgCard({ org }: { org: MyOrg }) {
  const api = useApi();
  const manager = isManager(org.role);
  const usage = useQuery({
    queryKey: ['usage', org.id],
    queryFn: () => api.usage(org.id),
    enabled: manager,
  });

  return (
    <Link to={`/organizations/${org.id}`}>
      <Card className="h-full hover:border-primary">
        <div className="flex items-start justify-between gap-3">
          <p className="font-medium">{org.name}</p>
          <Badge>{ROLE_LABELS[org.role]}</Badge>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{ORG_TYPE_LABELS[org.type]}</p>
        {manager && usage.data && (
          <p className="mt-3 text-sm tabular-nums text-muted-foreground">
            {usage.data.usage.maxMembers.toLocaleString()} of{' '}
            {usage.data.limits.maxMembers.toLocaleString()} members · {usage.data.planName}
          </p>
        )}
      </Card>
    </Link>
  );
}

/** Quizzes the learner can take, with their best score so far. */
function LearningSection() {
  const api = useApi();
  const learning = useQuery({ queryKey: ['learning'], queryFn: api.learning });
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Learning</h2>
        <Link to="/certificates" className="flex items-center gap-1.5 text-sm text-primary">
          <Award className="size-4" aria-hidden /> Your certificates
        </Link>
      </div>
      {learning.isError && <ErrorText error={learning.error} />}
      {learning.data?.quizzes.length === 0 && (
        <Card>
          <p className="text-sm text-muted-foreground">
            Self-assessments and contests from your organizations will appear here.
          </p>
        </Card>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {learning.data?.quizzes.map((q) => (
          <Card key={q.id} className="flex flex-col justify-between gap-4">
            <div>
              <p className="text-xs text-muted-foreground">{q.from}</p>
              <p className="mt-1 font-medium">{q.title}</p>
              {q.description && (
                <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{q.description}</p>
              )}
            </div>
            <div className="flex items-center justify-between gap-3">
              {q.best ? (
                <Link
                  to={`/attempts/${q.best.attemptId}`}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  Best: <span className="font-medium tabular-nums">{q.best.percent}%</span>
                  {q.best.passed && ' · passed'}
                </Link>
              ) : (
                <span className="text-sm text-muted-foreground">Not taken yet</span>
              )}
              <div className="flex items-center gap-2">
                <Link
                  to={`/q/${q.slug}`}
                  className="rounded-[var(--radius)] border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted"
                >
                  View details
                </Link>
                <Link
                  to={`/q/${q.slug}?start=1`}
                  className="rounded-[var(--radius)] bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
                >
                  {q.attempts ? 'Retake' : 'Start'}
                </Link>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}

/**
 * Nudges for account setup: a name for certificates until one is on file,
 * and a passkey until the learner has one (where this device supports them).
 */
function AccountReminders() {
  const me = useMe();
  const canUsePasskeys = useCanUsePasskeys();
  const passkeys = usePasskeys();
  const needsName = Boolean(me.data && !me.data.displayName);
  const needsPasskey = canUsePasskeys && passkeys.isSuccess && passkeys.data.length === 0;
  if (!needsName && !needsPasskey) return null;
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {needsName && (
        <Reminder
          icon={<UserRound className="size-5 text-primary" aria-hidden />}
          title="Add your name for certificates"
          text="Certificates are issued to the name on your account. Add it now so you're ready when you pass."
          to="/account"
          action="Add your name"
        />
      )}
      {needsPasskey && (
        <Reminder
          icon={<Fingerprint className="size-5 text-primary" aria-hidden />}
          title="Sign in faster with a passkey"
          text="Use your fingerprint, face or device PIN instead of waiting for an email code. Passkeys are more secure, too: they can't be phished."
          to="/account/security"
          action="Set up a passkey"
        />
      )}
    </div>
  );
}

function Reminder({
  icon,
  title,
  text,
  to,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
  to: string;
  action: string;
}) {
  return (
    <Card className="flex flex-col justify-between gap-3 border-primary/60">
      <div className="flex items-start gap-3">
        {icon}
        <div>
          <p className="font-medium">{title}</p>
          <p className="mt-1 text-sm text-muted-foreground">{text}</p>
        </div>
      </div>
      <Link
        to={to}
        className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-primary"
      >
        {action} <ArrowRight className="size-4" aria-hidden />
      </Link>
    </Card>
  );
}
